import type { ContractInterface} from 'ethers';
import { ethers } from 'ethers';
import type { Abi, Address, ContractFunctionArgs, ContractFunctionName } from 'viem';

import { getChainById } from '@shared/helpers/web3/chain';

interface EstimatePriceParams<
  TAbi extends Abi | readonly unknown[],
  TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'>,
> {
  abi: TAbi;
  chainId: number;
  contractAddress: Address;
  functionName: TFunctionName;
  walletAddress: Address;
  args?: ContractFunctionArgs<TAbi, 'nonpayable' | 'payable', TFunctionName> | [];
}

interface EstimatePriceResult {
  gasEstimate: bigint;
  gasPrice?: bigint;
}

const defaultGasEstimate = BigInt(500_000);

export const getEstimateGasPrice = async <
  TAbi extends Abi | readonly unknown[],
  TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'>,
>({
  abi,
  chainId,
  contractAddress,
  functionName,
  walletAddress,
  args = [],
}: EstimatePriceParams<TAbi, TFunctionName>): Promise<EstimatePriceResult> => {
  const chain = getChainById(chainId);
  const rpcUrl = chain.rpcUrls.default.http[0];

  const provider = new ethers.providers.JsonRpcProvider(rpcUrl).getSigner(walletAddress);
  const contract = new ethers.Contract(contractAddress, abi as ContractInterface, provider);

  try {
    if (!Array.isArray(args)) {
      throw new Error('Args should be array type');
    }

    const gasEstimateBigNumber = await contract.estimateGas[functionName](...args);
    const gasPriceBigNumber = await provider.getGasPrice();
    const gasEstimate = gasEstimateBigNumber.toBigInt();
    const gasPrice = gasPriceBigNumber.toBigInt();
    return { gasEstimate, gasPrice };
  } catch (error: any) {
    if (error.message) {
      console.error('Message error:', error.message);
    }

    if (error.transaction) {
      console.error('transaction error:', error.transaction);
    }

    if (error.error) {
      console.error('Contract error:', error.error.message);
    }

    return { gasEstimate: defaultGasEstimate };
  }
};
